import { createContext, useContext, useEffect, useState } from 'react'
import { PREFERENCE, onPreferencesReset, readPreference, writePreference } from '../state/prefs'
import { fileSlug } from '../state/download'

export const GridHalvesContext = createContext<boolean>(false)

function useIsHalfWidth(): boolean {
  return useContext(GridHalvesContext)
}

function slugifyCardTitle(title: string): string {
  const cleaned = title.replace(/\s*\(\d+\)$/, '').trim()
  return fileSlug(cleaned, 'card')
}

export function useCardVisibility(
  title?: string,
  id?: string,
  collapsible?: boolean,
): {
  canCollapse: boolean
  hidden: boolean
  toggleHidden: () => void
} {
  const isHalfWidth = useIsHalfWidth()
  const canCollapse = collapsible ?? (!isHalfWidth && Boolean(title))
  // Per page, since two pages may have a card of the same title. One
  // preference for all of them, so a reset clears them all.
  const cardKey = canCollapse
    ? `${location.hash.split('?')[0]}|${id ?? slugifyCardTitle(title ?? '')}`
    : undefined

  const [prevKey, setPrevKey] = useState(cardKey)
  const [hidden, setHidden] = useState(() => isHidden(cardKey))

  if (prevKey !== cardKey) {
    setPrevKey(cardKey)
    setHidden(isHidden(cardKey))
  }

  // A reset of preferences shows every card again, without a reload.
  useEffect(() => onPreferencesReset(() => setHidden(isHidden(cardKey))), [cardKey])

  function toggleHidden() {
    const next = !hidden
    setHidden(next)
    if (!cardKey) return
    const others = readHidden().filter((key) => key !== cardKey)
    writePreference(PREFERENCE.hiddenCards, JSON.stringify(next ? [...others, cardKey] : others))
  }

  return { canCollapse, hidden, toggleHidden }
}

function readHidden(): string[] {
  try {
    const parsed: unknown = JSON.parse(readPreference(PREFERENCE.hiddenCards) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((key) => typeof key === 'string') : []
  } catch {
    return []
  }
}

const isHidden = (cardKey: string | undefined) => Boolean(cardKey && readHidden().includes(cardKey))
